/**
 * WarmTargetInput — chip-shaped numeric override picker.
 *
 * Replaces the prelaunch boolean toggle. Tri-state: inherit (null) /
 * explicit 0 / explicit N≥1. Native <select> for keyboard nav and a11y;
 * styled to match the FlagToggles chip aesthetic.
 *
 * The legacy "Pre-launch requires Precache on" disable is dropped — the
 * resolver auto-clamps `precache=true` whenever `warmTarget>0`, so any
 * value the operator picks is valid. We surface this in the title text
 * so operators understand why precache flipped on.
 */

import styles from './FlagToggles.module.css';
import { WARM_TARGET_MAX } from './warmTargetConstants';

export interface WarmTargetInputProps {
  /** Override value: null = inherit pool default, otherwise an explicit count. */
  value: number | null;
  /** Pool default warmTarget — shown when override is null so operators can see what "inherit" means. */
  poolDefault: number;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  /** Maximum selectable warm count. Defaults to WARM_TARGET_MAX; pass pool's maxConcurrentLimit for tighter cap. */
  max?: number;
}

const INHERIT_TOKEN = '__inherit__';

export function WarmTargetInput({
  value,
  poolDefault,
  onChange,
  disabled,
  max = WARM_TARGET_MAX,
}: WarmTargetInputProps) {
  const explicit = value !== null;
  const display = explicit ? value : poolDefault;

  const cls = [
    styles.toggle,
    styles.warmTargetSelect,
    explicit ? (display > 0 ? styles.toggleOn : styles.toggleOff) : styles.toggleInh,
  ].join(' ');

  const stateLabel = explicit ? `${value}` : `inh (${poolDefault})`;
  const title = explicit
    ? `Explicit warm target: ${value}${value > 0 ? ' (auto-enables Precache)' : ''}. Pool default is ${poolDefault}. Pick inherit to clear.`
    : `Inherited from pool default (${poolDefault}). Pick a number to override.`;

  const options: Array<{ token: string; label: string }> = [
    { token: INHERIT_TOKEN, label: `inherit (${poolDefault})` },
  ];
  for (let n = 0; n <= max; n++) {
    options.push({ token: String(n), label: String(n) });
  }

  return (
    <label className={cls} title={title}>
      {/* aria-hidden on the visible spans so the SR announces only the
       * select's own aria-label — otherwise the chip's "warm inh (3)"
       * text bleeds into the announcement and reads as gibberish. */}
      <span className={styles.label} aria-hidden="true">warm</span>
      <span className={styles.state} aria-hidden="true">{stateLabel}</span>
      <select
        className={styles.warmTargetNativeSelect}
        value={explicit ? String(value) : INHERIT_TOKEN}
        aria-label="Warm target override"
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === INHERIT_TOKEN ? null : Math.max(0, Math.min(max, parseInt(v, 10))));
        }}
        disabled={disabled}
      >
        {options.map((opt) => (
          <option key={opt.token} value={opt.token}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
}
